import React, { useRef, useState } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import useDismissOnOutsidePointerDown from '../hooks/useDismissOnOutsidePointerDown';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  DayPicker,
  type DayPickerProps,
  type DayButtonProps,
  type DateRange,
  type Matcher,
} from 'react-day-picker';
import { dayjs, formatDate } from '@koob/dates';
import cn from '../utils/cn';
import {
  isWithinBounds,
  maskDateTyping,
  normalizePastedDate,
  normalizePastedRange,
  parseFlexibleDate,
  parseRangeText,
} from './koobDateText';
import {
  inputFrameClasses,
  inputRoundedClasses,
  inputSizeClasses,
  inputWarningClasses,
} from './KoobInputText';

export type KoobDateRange = DateRange;

export type KoobDateRangePreset = {
  label: string;
  range: () => DateRange;
};

const triggerVariants = cva(
  `inline-flex items-center whitespace-nowrap font-medium cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed ${inputFrameClasses} hover:bg-accent hover:text-accent-foreground relative`,
  {
    variants: {
      // Height/padding/text come from inputSizeClasses so the trigger matches
      // KoobInputText exactly; only the icon+gap layout is trigger-specific.
      size: {
        sm: `${inputSizeClasses.sm} gap-2 [&_svg]:h-3.5 [&_svg]:w-3.5`,
        md: `${inputSizeClasses.md} gap-2 [&_svg]:h-4 [&_svg]:w-4`,
      },
      rounded: inputRoundedClasses,
      block: {
        true: 'w-full',
        false: 'w-auto',
      },
      hasError: {
        true: 'border-red-500',
        false: '',
      },
      /** Same orange frame as KoobInputText `warning`; an error wins. */
      hasWarning: {
        true: inputWarningClasses,
        false: '',
      },
    },
    defaultVariants: {
      size: 'md',
      rounded: 'md',
      block: false,
      hasError: false,
      hasWarning: false,
    },
  },
);

const navButtonClass =
  'inline-flex h-8 w-8 items-center justify-center rounded-md border-none bg-transparent p-0 text-gray-600 cursor-pointer select-none transition-colors hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:h-4 [&_svg]:w-4';

const presetButtonClass =
  'rounded border-none bg-transparent px-2 py-1.5 text-left text-xs cursor-pointer transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

function CalendarDayButton({ modifiers, className, ...props }: DayButtonProps) {
  return (
    <button
      type="button"
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        'flex aspect-square h-8 w-8 items-center justify-center rounded-md border-none bg-transparent p-0 text-sm font-normal leading-none cursor-pointer select-none transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        'data-[selected-single=true]:bg-brand data-[selected-single=true]:text-brand-foreground data-[selected-single=true]:hover:bg-brand',
        'data-[range-start=true]:rounded-md data-[range-start=true]:bg-brand data-[range-start=true]:text-brand-foreground data-[range-start=true]:hover:bg-brand',
        'data-[range-end=true]:rounded-md data-[range-end=true]:bg-brand data-[range-end=true]:text-brand-foreground data-[range-end=true]:hover:bg-brand',
        'data-[range-middle=true]:rounded-none data-[range-middle=true]:bg-accent data-[range-middle=true]:text-accent-foreground',
        className,
      )}
      {...props}
    />
  );
}

type KoobCalendarProps = {
  mode: 'single' | 'range';
  selected?: Date | DateRange;
  onSelect: (selected?: Date | DateRange, triggerDate?: Date) => void;
  defaultMonth?: Date;
  numberOfMonths?: number;
  minDate?: Date;
  maxDate?: Date;
};

export const KoobCalendar = ({
  mode,
  selected,
  onSelect,
  defaultMonth,
  numberOfMonths = 1,
  minDate,
  maxDate,
}: KoobCalendarProps) => {
  const [month, setMonth] = useState<Date>(defaultMonth ?? new Date());

  const disabled: Matcher[] = [];
  if (minDate) disabled.push({ before: minDate });
  if (maxDate) disabled.push({ after: maxDate });

  const nav = (months: number) =>
    setMonth(dayjs(month).add(months, 'month').toDate());

  return (
    <div className="relative w-fit p-3">
      <div className="absolute left-3 top-3 z-raised flex items-center">
        <button
          type="button"
          className={navButtonClass}
          aria-label="Go to the previous month"
          onClick={() => nav(-1)}
        >
          <i className="fa-light fa-chevron-left" />
        </button>
      </div>
      <div className="absolute right-3 top-3 z-raised flex items-center">
        <button
          type="button"
          className={navButtonClass}
          aria-label="Go to the next month"
          onClick={() => nav(1)}
        >
          <i className="fa-light fa-chevron-right" />
        </button>
      </div>
      <DayPicker
        // ponytail: single component, mode narrowed by cast — rdp's union props
        // (mode/selected/onSelect) don't distribute over a dynamic mode prop
        {...({ mode, selected, onSelect } as DayPickerProps)}
        month={month}
        onMonthChange={setMonth}
        numberOfMonths={numberOfMonths}
        disabled={disabled.length > 0 ? disabled : undefined}
        captionLayout="dropdown"
        startMonth={minDate ?? new Date(1900, 0)}
        endMonth={maxDate ?? dayjs().add(120, 'month').toDate()}
        hideNavigation
        fixedWeeks
        showOutsideDays
        weekStartsOn={1}
        components={{ DayButton: CalendarDayButton }}
        classNames={{
          root: 'w-fit',
          months: 'relative flex flex-col gap-4 md:flex-row',
          month: 'flex w-full flex-col gap-4',
          month_caption: 'flex h-8 w-full items-center justify-center px-10',
          caption_label:
            'flex select-none items-center gap-1 whitespace-nowrap rounded-md px-1.5 py-1 text-sm font-medium',
          dropdowns: 'flex items-center justify-center',
          dropdown_root:
            'relative inline-flex items-center rounded-md transition-colors hover:bg-gray-100 focus-within:ring-1 focus-within:ring-ring',
          dropdown:
            'absolute inset-0 m-0 h-full w-full cursor-pointer appearance-none border-none p-0 opacity-0',
          chevron: 'h-3 w-3 text-gray-400',
          month_grid: 'w-full border-collapse',
          weekdays: 'flex',
          weekday:
            'w-8 flex-1 select-none text-[0.8rem] font-normal text-gray-500',
          week: 'mt-2 flex w-full',
          day: 'group/day relative aspect-square h-full w-full select-none p-0 text-center',
          range_start: 'rounded-l-md bg-accent',
          range_middle: 'rounded-none bg-accent',
          range_end: 'rounded-r-md bg-accent',
          selected: '',
          today: 'rounded-md font-semibold ring-1 ring-inset ring-gray-300',
          outside: 'text-gray-400 aria-selected:text-gray-400',
          disabled: 'text-gray-300',
          hidden: 'invisible',
        }}
      />
    </div>
  );
};

type KoobMonthPickerProps = {
  selected?: Date;
  onSelect: (date: Date) => void;
  defaultYear?: Date;
  minDate?: Date;
  maxDate?: Date;
};

export const KoobMonthPicker = ({
  selected,
  onSelect,
  defaultYear,
  minDate,
  maxDate,
}: KoobMonthPickerProps) => {
  const [displayedYear, setDisplayedYear] = useState<Date>(
    defaultYear ?? selected ?? new Date(),
  );

  const currentYearNum = displayedYear.getFullYear();

  const isPrevDisabled = Boolean(
    minDate && currentYearNum <= minDate.getFullYear(),
  );
  const isNextDisabled = Boolean(
    maxDate && currentYearNum >= maxDate.getFullYear(),
  );

  return (
    <div className="relative w-64 p-3">
      <div className="mb-2 flex items-center justify-between border-b border-solid border-gray-100 pb-2">
        <button
          type="button"
          className={navButtonClass}
          aria-label="Go to previous year"
          disabled={isPrevDisabled}
          onClick={() =>
            setDisplayedYear((prev) => dayjs(prev).subtract(1, 'year').toDate())
          }
        >
          <i className="fa-light fa-chevron-left" />
        </button>
        <span className="select-none text-sm font-semibold text-gray-800">
          {currentYearNum}
        </span>
        <button
          type="button"
          className={navButtonClass}
          aria-label="Go to next year"
          disabled={isNextDisabled}
          onClick={() =>
            setDisplayedYear((prev) => dayjs(prev).add(1, 'year').toDate())
          }
        >
          <i className="fa-light fa-chevron-right" />
        </button>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {Array.from({ length: 12 }, (_, i) => {
          const monthDate = new Date(currentYearNum, i, 1);
          const month = dayjs(monthDate);
          const isDisabled =
            Boolean(minDate && month.endOf('month').isBefore(minDate, 'day')) ||
            Boolean(maxDate && month.startOf('month').isAfter(maxDate, 'day'));
          const isSelected = selected ? month.isSame(selected, 'month') : false;
          const isToday = month.isSame(dayjs(), 'month');
          const monthLabel = month.format('MMM');

          return (
            <button
              key={i}
              type="button"
              disabled={isDisabled}
              data-selected={isSelected}
              data-today={isToday && !isSelected}
              className={cn(
                'flex h-9 items-center justify-center rounded-md border-none bg-transparent p-1 text-sm font-normal leading-none capitalize text-gray-700 cursor-pointer select-none transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                isSelected &&
                  'bg-brand font-semibold text-brand-foreground hover:bg-brand',
                isToday &&
                  !isSelected &&
                  'font-medium ring-1 ring-inset ring-gray-300',
                isDisabled &&
                  'pointer-events-none cursor-not-allowed opacity-30',
              )}
              onClick={() => {
                const date = new Date(monthDate);
                date.setHours(12, 0, 0, 0);
                onSelect(date);
              }}
            >
              {monthLabel}
            </button>
          );
        })}
      </div>
    </div>
  );
};

type PopoverShellProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: React.ReactNode;
  header?: React.ReactNode;
  children: React.ReactNode;
};

const PopoverShell = ({
  open,
  onOpenChange,
  trigger,
  header,
  children,
}: PopoverShellProps) => {
  const triggerRef = useRef<HTMLSpanElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useDismissOnOutsidePointerDown(open, () => onOpenChange(false), [
    triggerRef,
    contentRef,
  ]);

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={onOpenChange}>
      {/* display:contents keeps layout intact; the span only exists so the
          outside-pointerdown hook can tell trigger clicks from outside ones */}
      <span style={{ display: 'contents' }} ref={triggerRef}>
        {trigger}
      </span>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          ref={contentRef}
          align="start"
          sideOffset={4}
          // Keep focus in the trigger's text input so the user can keep typing
          onOpenAutoFocus={(event) => event.preventDefault()}
          className="z-popover w-auto rounded-[10px] border border-solid border-k-gray bg-white p-0 text-k-h shadow-[0_10px_30px_rgba(27,44,73,.12)] outline-none"
        >
          {header && (
            <div className="border-b border-solid border-gray-200 p-3">
              {header}
            </div>
          )}
          {children}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
};

type TriggerProps = VariantProps<typeof triggerVariants> & {
  label?: React.ReactNode;
  text?: string;
  placeholder?: string;
  disabled?: boolean;
  clearable?: boolean;
  /** When set, typing is auto-separated with this format's separators */
  format?: string;
  /** Changing it drops the draft, even when the committed text is identical */
  resetKey?: number;
  /** Shown under the field when the typed value is rejected */
  invalidLabel?: string;
  onPasteText?: (pasted: string) => string;
  onClear: () => void;
  /** Raw typed text on blur/Enter; false when the parent could not use it */
  onCommit: (text: string) => boolean;
  onOpen: () => void;
  onEnter: () => void;
};

const Trigger = React.forwardRef<HTMLInputElement, TriggerProps>(
  (
    {
      label,
      text,
      placeholder,
      size,
      rounded,
      block,
      hasError,
      hasWarning,
      disabled,
      clearable,
      format,
      resetKey,
      invalidLabel = 'Invalid date',
      onPasteText,
      onClear,
      onCommit,
      onOpen,
      onEnter,
    },
    ref,
  ) => {
    // Local draft of the typed text; null = show the formatted value
    const [editing, setEditing] = useState<string | null>(null);
    // A rejected draft survives the blur, so a value arriving from elsewhere
    // (calendar click, form reset) has to clear it
    const [invalid, setInvalid] = useState(false);
    const [seen, setSeen] = useState({ text, resetKey });
    if (seen.text !== text || seen.resetKey !== resetKey) {
      setSeen({ text, resetKey });
      setEditing(null);
      setInvalid(false);
    }
    const shown = editing ?? text ?? '';
    // A styled copy of the text is shown instead of the raw input (gray "→"
    // separator); it doubles as the width sizer so the field hugs its content
    // exactly. ponytail: kept while focused too, so "→" stays gray when typing.
    const overlay = shown.length > 0;

    return (
      <span
        className={cn(
          'flex-col items-start gap-1',
          block ? 'flex w-full' : 'inline-flex',
        )}
      >
        <PopoverPrimitive.Anchor asChild>
          <div
            className={cn(
              triggerVariants({
                size,
                rounded,
                block,
                hasError: hasError || invalid,
                hasWarning: hasWarning && !(hasError || invalid),
              }),
              'focus-within:border-k-h focus-within:ring-4 focus-within:ring-navy-100',
              clearable && text && 'pr-8',
              disabled && 'pointer-events-none opacity-50',
            )}
            onClick={(event) => {
              if (disabled) return;
              // Clicks on the frame/icon should focus the text input too
              event.currentTarget.querySelector('input')?.focus();
              onOpen();
            }}
          >
            <i className="fa-light fa-calendar shrink-0 text-gray-500" />
            {label && <span className="text-gray-500">{label}</span>}
            <span
              className={cn('relative inline-flex', block && 'w-full min-w-0')}
            >
              <span
                aria-hidden
                className={cn(
                  'pointer-events-none select-none whitespace-pre',
                  !overlay && 'invisible',
                )}
              >
                {(shown || placeholder || '')
                  .split(' → ')
                  .map((part, index) => (
                    <React.Fragment key={index}>
                      {index > 0 && (
                        <span className="font-normal text-gray-400">
                          {' → '}
                        </span>
                      )}
                      {part}
                    </React.Fragment>
                  ))}
              </span>
              <input
                ref={ref}
                type="text"
                disabled={disabled}
                aria-invalid={hasError || invalid || undefined}
                value={shown}
                placeholder={placeholder}
                className={cn(
                  'absolute inset-0 m-0 w-full border-none bg-transparent p-0 font-medium text-gray-900 outline-none placeholder:font-normal placeholder:text-gray-400',
                  // The styled copy is what the user reads, so the real text is
                  // hidden — the caret still has to be visible to type with
                  overlay && 'text-transparent caret-gray-900',
                )}
                // ponytail: inline outline beats the UA focus ring — the app has
                // no preflight, so `outline-none` alone loses to it in Chrome
                style={{ font: 'inherit', outline: 'none', boxShadow: 'none' }}
                onChange={(event) => {
                  const { value } = event.target;
                  const masked = format
                    ? maskDateTyping(shown, value, format)
                    : value;
                  // The mask may only extend what is displayed. Editing mid-value
                  // would otherwise re-group every digit and throw the caret to
                  // the end; the raw text is parsed on blur anyway.
                  setInvalid(false);
                  setEditing(masked.startsWith(shown) ? masked : value);
                }}
                onPaste={(event) => {
                  if (!onPasteText) return;
                  event.preventDefault();
                  setInvalid(false);
                  setEditing(onPasteText(event.clipboardData.getData('text')));
                }}
                onBlur={() => {
                  if (editing === null) return;
                  if (onCommit(editing)) {
                    setEditing(null);
                    setInvalid(false);
                  } else {
                    setInvalid(true);
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.currentTarget.blur(); // commits via onBlur
                    onEnter();
                  } else if (event.key === 'Escape') {
                    setEditing(null);
                    setInvalid(false);
                  } else if (event.key === 'ArrowDown') {
                    onOpen();
                  }
                }}
              />
            </span>
            {clearable && text && !disabled && (
              <span
                role="button"
                tabIndex={-1}
                aria-label="Clear date"
                className="absolute right-1.5 top-1/2 flex h-5 w-5 -translate-y-1/2 cursor-pointer items-center justify-center rounded text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900"
                onClick={(event) => {
                  event.stopPropagation();
                  setEditing(null);
                  setInvalid(false);
                  onClear();
                }}
              >
                <i className="fa-light fa-xmark" />
              </span>
            )}
          </div>
        </PopoverPrimitive.Anchor>
        {invalid && (
          <p role="alert" className="m-0 text-sm text-red-500">
            {invalidLabel}
          </p>
        )}
      </span>
    );
  },
);
Trigger.displayName = 'KoobInputDateTrigger';

type KoobInputDateCommonProps = VariantProps<typeof triggerVariants> & {
  /** Muted prefix rendered inside the trigger, e.g. "Creation:" */
  label?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  clearable?: boolean;
  minDate?: Date;
  maxDate?: Date;
  /** dayjs format used in the trigger */
  format?: string;
  /** Rendered above the calendar, e.g. a <KoobSegmentedControl /> */
  header?: React.ReactNode;
};

type KoobInputDateProps = KoobInputDateCommonProps & {
  value?: Date | null;
  onChange: (date: Date | null) => void;
  /** 'date' (default) for day picker, 'month' for month picker */
  view?: 'date' | 'month';
  /** Shown instead of "Invalid date/month" when a well-formed value is before minDate */
  beforeMinLabel?: string;
};

// Typed text → Date, or why it was rejected
const parseTyped = (
  text: string,
  format: string,
  minDate?: Date,
  maxDate?: Date,
): Date | 'invalid' | 'beforeMin' => {
  const date = parseFlexibleDate(text, format);
  if (!date) return 'invalid';
  if (isWithinBounds(date, format, minDate, maxDate)) return date;
  return isWithinBounds(date, format, minDate) ? 'invalid' : 'beforeMin';
};

export const KoobInputDate = React.forwardRef<
  HTMLInputElement,
  KoobInputDateProps
>(
  (
    {
      value,
      onChange,
      view = 'date',
      beforeMinLabel,
      label,
      placeholder,
      disabled,
      clearable = true,
      minDate,
      maxDate,
      format,
      header,
      size,
      rounded,
      block,
      hasError,
      hasWarning,
    },
    ref,
  ) => {
    const [open, setOpen] = useState(false);
    // Bumped on every pick so a draft is dropped even when the picked value
    // equals the current one and `text` therefore does not change
    const [picks, setPicks] = useState(0);
    const [beforeMin, setBeforeMin] = useState(false);
    const effectiveFormat =
      format ?? (view === 'month' ? 'MM/YYYY' : 'DD/MM/YYYY');
    const effectivePlaceholder =
      placeholder ??
      (view === 'month' ? 'Select or type a month' : 'Select or type a date');

    return (
      <PopoverShell
        open={open}
        onOpenChange={setOpen}
        header={header}
        trigger={
          <Trigger
            ref={ref}
            label={label}
            text={value ? formatDate(value, effectiveFormat) : undefined}
            placeholder={effectivePlaceholder}
            size={size}
            rounded={rounded}
            block={block}
            hasError={hasError}
            hasWarning={hasWarning}
            disabled={disabled}
            clearable={clearable}
            format={effectiveFormat}
            resetKey={picks}
            invalidLabel={
              (beforeMin && beforeMinLabel) ||
              (view === 'month' ? 'Invalid month' : 'Invalid date')
            }
            onPasteText={(pasted) =>
              normalizePastedDate(pasted, effectiveFormat)
            }
            onClear={() => onChange(null)}
            onOpen={() => setOpen(true)}
            onEnter={() => setOpen(false)}
            onCommit={(text) => {
              // Emptying the field only clears when the input is clearable
              if (!text.trim()) {
                if (clearable) onChange(null);
                return true;
              }
              const parsed = parseTyped(
                text,
                effectiveFormat,
                minDate,
                maxDate,
              );
              setBeforeMin(parsed === 'beforeMin');
              if (!(parsed instanceof Date)) return false;
              parsed.setHours(12, 0, 0, 0);
              onChange(parsed);
              return true;
            }}
          />
        }
      >
        {view === 'month' ? (
          <KoobMonthPicker
            selected={value ?? undefined}
            defaultYear={value ?? undefined}
            minDate={minDate}
            maxDate={maxDate}
            onSelect={(date) => {
              setPicks((count) => count + 1);
              onChange(date);
              setOpen(false);
            }}
          />
        ) : (
          <KoobCalendar
            mode="single"
            selected={value ?? undefined}
            defaultMonth={value ?? undefined}
            minDate={minDate}
            maxDate={maxDate}
            onSelect={(selected) => {
              // Clicking the day that is already selected deselects it in
              // single mode, and the calendar reports nothing back. That is a
              // path to empty like any other, so `clearable` governs it too —
              // as it already does for emptying the text field. Without this a
              // non-clearable picker lost its value on a second click of the
              // same day, and every consumer reading it as a date got an
              // Invalid Date it never expected.
              if (!selected && !clearable) {
                setOpen(false);
                return;
              }

              // ponytail: normalize to local noon like the legacy pickers did —
              // keeps toISOString() on the same calendar day in every timezone
              const date = selected ? new Date(selected as Date) : null;
              date?.setHours(12, 0, 0, 0);
              setPicks((count) => count + 1);
              onChange(date);
              setOpen(false);
            }}
          />
        )}
      </PopoverShell>
    );
  },
);
KoobInputDate.displayName = 'KoobInputDate';

export const koobDateRangePresets = (): KoobDateRangePreset[] => [
  { label: 'Today', range: () => ({ from: new Date(), to: new Date() }) },
  {
    label: 'Yesterday',
    range: () => ({
      from: dayjs().subtract(1, 'day').toDate(),
      to: dayjs().subtract(1, 'day').toDate(),
    }),
  },
  {
    label: 'Last 3 days',
    range: () => ({
      from: dayjs().subtract(2, 'day').toDate(),
      to: new Date(),
    }),
  },
  {
    label: 'Last 7 days',
    range: () => ({
      from: dayjs().subtract(6, 'day').toDate(),
      to: new Date(),
    }),
  },
  {
    label: 'Last 30 days',
    range: () => ({
      from: dayjs().subtract(29, 'day').toDate(),
      to: new Date(),
    }),
  },
  {
    label: 'This month',
    range: () => ({
      from: dayjs().startOf('month').toDate(),
      to: dayjs().endOf('month').toDate(),
    }),
  },
  {
    label: 'Last month',
    range: () => ({
      from: dayjs().subtract(1, 'month').startOf('month').toDate(),
      to: dayjs().subtract(1, 'month').endOf('month').toDate(),
    }),
  },
];

// Legacy range pickers emitted [startOfDay, endOfDay] so date-only filters
// include the whole last day — keep that contract.
const normalizeRange = (range?: DateRange | null): DateRange | null =>
  range?.from
    ? {
        from: dayjs(range.from).startOf('day').toDate(),
        to: range.to ? dayjs(range.to).endOf('day').toDate() : undefined,
      }
    : null;

type KoobInputDateRangeProps = KoobInputDateCommonProps & {
  value?: DateRange | null;
  onChange: (range: DateRange | null) => void;
  /** Quick-select entries on the left side; pass [] to hide the sidebar */
  presets?: KoobDateRangePreset[];
  numberOfMonths?: number;
  /** When true, calendar popover mounts in open state */
  defaultOpen?: boolean;
  /** Callback fired whenever the popover open state changes */
  onOpenChange?: (open: boolean) => void;
  /** Callback fired when a complete date range is selected */
  onComplete?: (range: DateRange) => void;
};

export const KoobInputDateRange = React.forwardRef<
  HTMLInputElement,
  KoobInputDateRangeProps
>(
  (
    {
      value,
      onChange,
      label,
      placeholder = 'Select a period',
      disabled,
      clearable = true,
      minDate,
      maxDate,
      format = 'DD/MM/YYYY',
      header,
      presets = koobDateRangePresets(),
      numberOfMonths = 2,
      size,
      rounded,
      block,
      hasError,
      defaultOpen = false,
      onOpenChange: externalOnOpenChange,
      onComplete,
    },
    ref,
  ) => {
    const [open, setOpen] = useState(defaultOpen);
    // Draft holds the in-progress selection (first click of a range) so the
    // calendar stays responsive; onChange only ever emits complete ranges.
    const [draft, setDraft] = useState<DateRange | undefined>(
      defaultOpen ? (value ?? undefined) : undefined,
    );
    const [picks, setPicks] = useState(0);

    const text = value?.from
      ? `${formatDate(value.from, format)} → ${
          value.to ? formatDate(value.to, format) : '…'
        }`
      : undefined;

    const commitText = (raw: string) => {
      if (!raw.trim()) {
        // Emptying the field only clears when the input is clearable
        if (clearable) {
          setDraft(undefined);
          onChange(null);
        }
        return true;
      }
      const parsed = parseRangeText(raw, format);
      if (!parsed) return false;
      const [from, to] = parsed;
      if (
        from > to ||
        !isWithinBounds(from, format, minDate, maxDate) ||
        !isWithinBounds(to, format, minDate, maxDate)
      ) {
        return false;
      }
      const range = normalizeRange({ from, to });
      setDraft(range ?? undefined);
      onChange(range);
      return true;
    };

    const handleOpenChange = (nextOpen: boolean) => {
      if (nextOpen) setDraft(value ?? undefined);
      setOpen(nextOpen);
      externalOnOpenChange?.(nextOpen);
    };

    return (
      <PopoverShell
        open={open}
        onOpenChange={handleOpenChange}
        header={header}
        trigger={
          <Trigger
            ref={ref}
            label={label}
            text={text}
            placeholder={placeholder}
            size={size}
            rounded={rounded}
            block={block}
            hasError={hasError}
            disabled={disabled}
            clearable={clearable}
            resetKey={picks}
            invalidLabel="Invalid period"
            // No format: the mask groups digits for one date and would
            // mangle the "from → to" string, so ranges only normalize pastes
            onPasteText={(pasted) => normalizePastedRange(pasted, format)}
            onClear={() => {
              setDraft(undefined);
              onChange(null);
            }}
            onCommit={commitText}
            onOpen={() => handleOpenChange(true)}
            onEnter={() => handleOpenChange(false)}
          />
        }
      >
        <div className="flex">
          {presets.length > 0 && (
            <div className="flex min-w-[140px] flex-col border-r border-solid border-gray-200 p-2">
              {presets.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  className={presetButtonClass}
                  onClick={() => {
                    const range = normalizeRange(preset.range());
                    setDraft(range ?? undefined);
                    setPicks((count) => count + 1);
                    onChange(range);
                    handleOpenChange(false);
                    if (range) onComplete?.(range);
                  }}
                >
                  {preset.label}
                </button>
              ))}
              {clearable && (
                // Separator lives on the wrapper so the button keeps border-none
                <div className="mt-1 flex flex-col border-0 border-t border-solid border-gray-200 pt-2">
                  <button
                    type="button"
                    className={cn(presetButtonClass, 'text-gray-500')}
                    onClick={() => {
                      setDraft(undefined);
                      onChange(null);
                      handleOpenChange(false);
                    }}
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
          )}
          <KoobCalendar
            mode="range"
            selected={draft}
            defaultMonth={draft?.from ?? value?.from ?? undefined}
            numberOfMonths={numberOfMonths}
            minDate={minDate}
            maxDate={maxDate}
            onSelect={(selected, triggerDate) => {
              // Only an in-progress draft (from set, to unset) completes the
              // range. Otherwise start fresh from the clicked day: rdp returns
              // a same-day {from, to} on the first click of an empty range,
              // and a click on a complete range should restart, not adjust
              // the nearest edge.
              const inProgress = draft?.from && !draft.to;
              const range = inProgress
                ? (selected as DateRange | undefined)
                : triggerDate
                  ? { from: triggerDate, to: undefined }
                  : undefined;
              setDraft(range);
              if (range?.from && range?.to) {
                setPicks((count) => count + 1);
                const normalized = normalizeRange(range);
                onChange(normalized);
                handleOpenChange(false);
                if (normalized) onComplete?.(normalized);
              }
            }}
          />
        </div>
      </PopoverShell>
    );
  },
);
KoobInputDateRange.displayName = 'KoobInputDateRange';
